import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useParams, Link, useNavigate } from "react-router-dom";
import { IconArrowLeft, IconEdit, IconTrashFilled, IconHeart, IconUser, IconCurrentLocation } from "@tabler/icons-react";
import { getAthleteById, deleteAthlete } from "../../db";
import LoadinPage from "../../Components/LoadingPage";

const Detail = () => {
    const { id } = useParams(); // ✅ Get athlete ID from URL
    const navigate = useNavigate(); // ✅ For redirecting if athlete is not found
    const { t } = useTranslation();

    const [athlete, setAthlete] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAthlete = async () => {
            const foundAthlete = await getAthleteById(id);
            setLoading(false);

            if (foundAthlete) {
                setAthlete(foundAthlete);
            } else {
                navigate("/athletes", { state: { error: t("athletesDetail.notFound") } });
            }
        };
        fetchAthlete();
    }, [id, navigate]);

    const handleDelete = async (id) => {
        if (window.confirm(t("athletes.removeConfirmation"))) {
            await deleteAthlete(id);
            navigate("/athletes", { state: { error: t("athletesDetail.removed") } });
        }
    };

    if (loading) return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center pb-4 w-full">
                <div className="shrink-0 w-10">
                    <Link to="/athletes">
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("athletesDetail.backToList")}</span>
                    </Link>
                </div>
                <div className="grow text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                </div>
                <div className="shrink-0 w-10">
                </div>
            </div>

            <div className="grow overflow-y-auto overflow-x-hidden">
                <LoadinPage />
            </div>
        </div>
    );

    return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center pb-4 w-full">
                <div className="shrink-0 w-10">
                    <Link to="/athletes">
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("athletesDetail.backToList")}</span>
                    </Link>
                </div>
                <h1 className="grow text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                    {athlete.nickname || `${athlete.firstname} ${athlete.lastname}`}
                </h1>
                <div className="shrink-0 w-10">
                    <Link to={`/athletes/${athlete.id}/edit`}>
                        <IconEdit className="w-6 h-auto" />
                        <span className="sr-only">{t("athletesDetail.edit")}</span>
                    </Link>
                </div>
            </div>

            <div className="text-xs flex flex-col mb-6 sm:flex-row gap-2">
                <Link className="button-primary" to={`/athletes/${athlete.id}/shooting`}>
                    <IconCurrentLocation className="w-4 h-auto transform skew-x-12" />
                    {t("athletesDetail.goShooting")}
                </Link>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <section className="flex flex-col gap-4" data-type="PersonalInfo">
                    <h2 className="text-lg uppercase font-black transform -skew-x-12 border-b-2 border-[var(--light-color)] flex items-center gap-2">
                        <IconUser className="w-6 h-auto transform skew-x-12" />
                        <span className="grow">{t("athleteDetail.personalInfo")}</span>
                    </h2>
                    <div>
                        <table className="simple-table">
                            <tbody>
                            {athlete.firstname && (
                                <tr>
                                    <th>{t("athletes.firstname")}</th>
                                    <td>{athlete.firstname}</td>
                                </tr>
                            )}
                            {athlete.lastname && (
                                <tr>
                                    <th>{t("athletes.lastname")}</th>
                                    <td>{athlete.lastname}</td>
                                </tr>
                            )}
                            {athlete.email && (
                                <tr>
                                    <th>{t("athletes.email")}</th>
                                    <td>{athlete.email}</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </section>
                <section className="flex flex-col gap-4" data-type="Health">
                    <h2 className="text-lg uppercase font-black transform -skew-x-12 border-b-2 border-[var(--light-color)] flex items-center gap-2">
                        <IconHeart className="w-6 h-auto transform skew-x-12" />
                        <span className="grow">{t("athleteDetail.Health")}</span>
                    </h2>
                    <div>
                        <table className="simple-table">
                            <tbody>
                                {athlete.weight && (
                                    <tr>
                                        <th>{t("athletes.weight")}</th>
                                        <td>{athlete.weight} kg</td>
                                    </tr>
                                )}
                                {athlete.height && (
                                    <tr>
                                        <th>{t("athletes.height")}</th>
                                        <td>{athlete.height} cm</td>
                                    </tr>
                                )}
                                {athlete.weight && athlete.height && (
                                    <tr>
                                        <th>{t("athletes.bmi")}</th>
                                        <td>{(athlete.weight / ((athlete.height / 100) ** 2)).toFixed(2)}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <div className="mt-20">
                <button className="button-secondary" onClick={() => handleDelete(athlete.id)}>
                    <IconTrashFilled className="w-4 h-auto transform skew-x-12" />
                    {t("athletesDetail.remove")}
                </button>
            </div>
        </div>
    );
};

export default Detail;
